/**
 * coPrice.js: the ONE change-order line price reader (IDI-492), mirrored from
 * backend internal/coprice. Both are tested against the same fixture
 * (backend-go/internal/coprice/testdata/agreement_cases.json), so the dealer's
 * read-only screens show exactly what the server applies, invoices and shows
 * the customer.
 *
 * RULED (Adrian, 2026-10-11, C-03 / C-04):
 *   - A line whose price can't be read is UNPRICED. A changed unpriced line
 *     is no price change. An added unpriced line is $0 and flags the change
 *     order "needs re-pricing" (it can't be sent or applied until priced).
 *   - The net is ALWAYS computed from the lines; a stored netTotal is never
 *     trusted.
 *
 * Use this on READ-ONLY screens (job CO list, Review & Send). The builder
 * keeps the live margin engine (changeOrderEngine.computeChangeOrder) while
 * editing: re-saving stamps that price on the line, which makes it priced
 * here too (ruled with the operator, 2026-10-15).
 *
 * Money is integer cents; fractional values round half away from zero.
 */

/** Integer cents, or null when absent, null, empty, non-numeric or non-finite. */
export function cents(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return null;
  const r = Math.sign(n) * Math.round(Math.abs(n)); // half away from zero
  return r + 0; // normalise -0
}

/**
 * The line's revised price in cents, or null when UNPRICED.
 * camelCase `lineTotal` first, then `line_total`, then `amount`.
 * `lineTotal: null` beside `line_total: 0` is the pre-#533 bug shape: UNPRICED.
 */
export function linePrice(line) {
  if (!line || typeof line !== 'object') return null;
  if (Object.prototype.hasOwnProperty.call(line, 'lineTotal') && line.lineTotal === null
    && cents(line.line_total) === 0) {
    return null;
  }
  for (const k of ['lineTotal', 'line_total', 'amount']) {
    const v = cents(line[k]);
    if (v !== null) return v;
  }
  return null;
}

function originalPrice(line) {
  return cents(line && line.original ? line.original.total : null);
}

/** One line's contribution to the net: { cents, unpriced, counted, status }. */
export function lineDelta(line) {
  const status = String((line && line.status) || '').toLowerCase();
  const out = { status, cents: 0, unpriced: false, counted: false };
  if (status === 'added') {
    out.counted = true;
    const p = linePrice(line);
    if (p === null) out.unpriced = true; else out.cents = p;
  } else if (status === 'changed') {
    out.counted = true;
    const p = linePrice(line);
    if (p === null) { out.unpriced = true; return out; } // no price change
    out.cents = p - (originalPrice(line) || 0);
  } else if (status === 'cancelled' || status === 'removed') {
    out.counted = true;
    let o = originalPrice(line);
    if (o === null) o = linePrice(line) || 0;
    out.cents = -Math.abs(o) + 0;
  }
  return out;
}

/** The change order's net from its lines, and whether it needs re-pricing. */
export function summarizeChangeOrder(lines) {
  let netCents = 0;
  let unpricedAdded = 0;
  let unpricedChanged = 0;
  (Array.isArray(lines) ? lines : []).forEach((l) => {
    const d = lineDelta(l);
    if (!d.counted) return;
    netCents += d.cents;
    if (d.unpriced) {
      if (d.status === 'added') unpricedAdded += 1; else unpricedChanged += 1;
    }
  });
  return { netCents, unpricedAdded, unpricedChanged, needsRepricing: unpricedAdded > 0 };
}

/** A change order's line list, whichever key it is stored under. */
export function coLines(co) {
  if (!co) return [];
  for (const k of ['lines', 'lineItems', 'line_items']) {
    if (Array.isArray(co[k]) && co[k].length) return co[k];
  }
  return [];
}
